import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError,
  isRouteErrorResponse,
  useNavigate,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { useEffect } from "react";
import { embeddedHref } from "./utils/embedded-navigation";
import BootSplash from "./components/shared/BootSplash";
import { BOOT_SPLASH_CSS } from "./lib/boot-splash";

function App() {
  // App Bridge tells Remix to change routes by firing `shopify:navigate`.
  // This must stay as client navigation: posting back through /app/redirect
  // would loop (Shopify redirect → this event → another redirect).
  const navigate = useNavigate();
  const location = useLocation();
  const isMembershipPreview = location.pathname.includes(
    "/membership-modal/preview",
  );

  useEffect(() => {
    const handleNavigate = (event) => {
      const href = event.target?.getAttribute?.("href");
      if (!href) return;
      const next = embeddedHref(href, window.location.search, window.location.origin);
      if (!next) return;
      // Without this, App Bridge also reloads the iframe. That document
      // request has no session-token header and authenticate.admin bounces
      // to OAuth.
      event.preventDefault();
      event.stopPropagation();
      navigate(next);
    };

    document.addEventListener("shopify:navigate", handleNavigate, true);
    return () => {
      document.removeEventListener("shopify:navigate", handleNavigate, true);
    };
  }, [navigate]);

  return (
    <html style={{ width: "100%", height: "100%" }}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="preconnect" href="https://cdn.shopify.com/" />
        {!isMembershipPreview && (
          <link
            rel="preload"
            href="https://cdn.shopify.com/shopifycloud/polaris.js"
            as="script"
          />
        )}
        <style dangerouslySetInnerHTML={{ __html: BOOT_SPLASH_CSS }} />
        <link
          rel="stylesheet"
          href="https://cdn.shopify.com/static/fonts/inter/v4/styles.css"
        />
        <Meta />
        <Links />
      </head>
      <body>
        {!isMembershipPreview && <BootSplash />}
        {!isMembershipPreview && (
          <script src="https://cdn.shopify.com/shopifycloud/polaris.js" />
        )}
        <Outlet />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();

  // Capture the error to Sentry
  captureRemixErrorBoundaryError(error);

  if (isRouteErrorResponse(error)) {
    return (
      <html>
        <head>
          <title>Error {error.status}</title>
          <Meta />
          <Links />
        </head>
        <body>
          <h1>
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
          <Scripts />
        </body>
      </html>
    );
  }

  const errorMessage = error instanceof Error ? error.message : "Unknown error";

  return (
    <html>
      <head>
        <title>Error!</title>
        <Meta />
        <Links />
      </head>
      <body>
        <h1>Oops! Something went wrong</h1>
        <p>{errorMessage}</p>
        <Scripts />
      </body>
    </html>
  );
}
